import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/api';
import './Auth.css';

function SignUp() {
  const [signUpData, setSignUpData] = useState({ 
    username: '',
    email: '', 
    password: '', 
    confirmPassword: '' 
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  const handleSignUpChange = (e) => {
    const { name, value } = e.target;
    setSignUpData({
      ...signUpData,
      [name]: value
    });
    // Clear error when user starts typing again
    if (error) setError('');
  };
  
  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (signUpData.password !== signUpData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate password strength (optional)
    if (signUpData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Make API request to register endpoint
      const response = await fetch(buildApiUrl('api/users/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: signUpData.username,
          email: signUpData.email,
          password: signUpData.password
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to sign up');
      }
      
      // Show success feedback
      console.log('Registration successful:', data);
      
      // Automatically log in or redirect to sign in
      navigate('/signin', { 
        state: { 
          message: 'Account created successfully! Please sign in.' 
        } 
      });
      
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container signup-container">
        <div className="nebula-bg auth-bg"></div>
        
        <div className="auth-card">
          <Link to="/" className="auth-logo">
            <span className="logo-text">COSMIC<span>BUILDER</span></span>
          </Link>
          
          <h1>Create Account</h1>
          <p className="auth-subtitle">Join the cosmic community today</p>
          
          {error && (
            <div className="auth-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}
          
          <form onSubmit={handleSignUpSubmit} className="auth-form">
            <div className="form-group-auth">
              <label htmlFor="signup-username">Username</label>
              <input
                type="text"
                id="signup-username"
                name="username"
                value={signUpData.username}
                onChange={handleSignUpChange}
                required
                placeholder="Choose a username"
                disabled={loading}
              />
            </div>
            
            <div className="form-group-auth">
              <label htmlFor="signup-email">Email</label>
              <input
                type="email"
                id="signup-email"
                name="email"
                value={signUpData.email}
                onChange={handleSignUpChange}
                required
                placeholder="Enter your email"
                disabled={loading}
              />
            </div>
            
            <div className="form-group-auth">
              <label htmlFor="signup-password">Password</label>
              <input
                type="password"
                id="signup-password"
                name="password"
                value={signUpData.password}
                onChange={handleSignUpChange}
                required
                placeholder="Create a password"
                disabled={loading}
              />
            </div>
            
            <div className="form-group-auth">
              <label htmlFor="signup-confirm-password">Confirm Password</label>
              <input
                type="password"
                id="signup-confirm-password"
                name="confirmPassword"
                value={signUpData.confirmPassword}
                onChange={handleSignUpChange}
                required
                placeholder="Confirm your password"
                disabled={loading}
              />
            </div>
            
            <div className="form-options">
              <div className="terms-agreement">
                <input type="checkbox" id="agree-terms" required />
                <label htmlFor="agree-terms">
                  I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>
                </label>
              </div>
            </div>
            
            <button 
              type="submit" 
              className={`auth-submit-btn ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              <span className="btn-glow"></span>
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
          
          <div className="auth-separator">
            <span>OR</span>
          </div>
          
          <div className="social-auth">
            <button className="social-btn google" disabled={loading}>
              <i className="fab fa-google"></i> Sign up with Google
            </button>
            <button className="social-btn github" disabled={loading}>
              <i className="fab fa-github"></i> Sign up with GitHub
            </button>
          </div>
          
          <div className="auth-redirect">
            Already have an account? 
            <Link to="/signin">Sign In</Link>
          </div>
        </div>
        
        {/* Add animated background elements */}
        <div className="floating-elements auth-floating">
          <div className="floating-element planet-small"></div>
          <div className="floating-element star-cluster-small"></div>
        </div>
        
        <div className="auth-particles">
          {[...Array(20)].map((_, index) => (
            <div key={index} className="particle"></div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SignUp; 